import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAllRequirements, updateRequirement } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import {
  GARMENT_TYPE,
  PRIORITY,
  REQUIREMENT_CATEGORY,
  REQUIREMENT_STATUS,
  WORKFLOW_STAGE,
  formatDate,
  formatINR,
  label,
} from '../../constants/garmentCodes';

const STATUS = { SUBMITTED: 2, UNDER_REVIEW: 3, CLARIFICATION: 4, ACCEPTED: 5 };
const REVIEWABLE = [STATUS.SUBMITTED, STATUS.UNDER_REVIEW];

export default function Requirements() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const engagementId = params.get('engagementId') || '';
  const [selected, setSelected] = useState(null);

  const requirements = useSelector((state) => state.requirement.requirements);
  const loading = useSelector((state) => state.requirement.loading);

  useEffect(() => {
    dispatch(getAllRequirements({ engagementId, status: '' }));
  }, [dispatch, engagementId]);

  const setStatus = (req, status, stage) => {
    dispatch(updateRequirement({ payload: { id: req.id, status, ...(stage ? { stage } : {}) } }));
    setSelected(null);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Header title="Product Requirements" breadcrumbs={[{ workspace: true }, { label: 'Requirements' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">


        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Requirements"
            columns={[
              { id: 'reqId', label: 'CODE', align: 'left' },
              { id: 'brand', label: 'BRAND', align: 'left' },
              { id: 'product', label: 'PRODUCT', align: 'left' },
              { id: 'garmentType', label: 'GARMENT TYPE', align: 'left' },
              { id: 'qty', label: 'QTY', align: 'right' },
              { id: 'targetCost', label: 'TARGET COST', align: 'right' },
              { id: 'launchDate', label: 'LAUNCH DATE', align: 'left' },
              { id: 'stage', label: 'STAGE', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'actions', label: 'ACTIONS', align: 'left', disableFilter: true },
            ]}
            rows={requirements.map((req) => {
              const status = label(REQUIREMENT_STATUS, req.status);
              const open = () => !req.__optimistic && setSelected(req);
              return {
                id: req.id,
                rColumns: [
                  <div key="id" label={req.code || ''} onClick={open} className={`flex items-center w-full h-full px-2 py-1 font-mono text-[12px] text-[#1b3a6b] font-medium cursor-pointer ${req.__optimistic ? 'opacity-60' : ''}`}>{req.code || `#${req.id}`}</div>,
                  <div key="br" label={req.brandName || ''} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] font-medium text-gray-800 cursor-pointer">{req.brandName || '—'}</div>,
                  <div key="prod" label={req.name || ''} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-700 cursor-pointer">{req.name}</div>,
                  <div key="gtype" label={label(GARMENT_TYPE, req.garmentType)} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600 cursor-pointer">{label(GARMENT_TYPE, req.garmentType)}</div>,
                  <div key="qty" label={String(req.quantity ?? '')} onClick={open} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-gray-700 cursor-pointer">{req.quantity ?? '—'}</div>,
                  <div key="cost" label={formatINR(req.targetCost)} onClick={open} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-gray-700 cursor-pointer">{formatINR(req.targetCost)}</div>,
                  <div key="date" label={req.launchDate || ''} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600 cursor-pointer">{formatDate(req.launchDate)}</div>,
                  <div key="stage" label={label(WORKFLOW_STAGE, req.stage)} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600 cursor-pointer">{label(WORKFLOW_STAGE, req.stage)}</div>,
                  <div key="stat" label={status} onClick={open} className="flex items-center w-full h-full px-2 py-1 cursor-pointer"><Badge status={status} /></div>,
                  <div key="act" label="" className="flex items-center gap-2 w-full h-full px-2 py-1">
                    {REVIEWABLE.includes(req.status) && (
                      <button onClick={() => setStatus(req, STATUS.ACCEPTED, 3)} className="text-[11px] text-green-600 font-medium hover:underline">Accept</button>
                    )}
                    {req.status !== STATUS.CLARIFICATION && !req.__optimistic && (
                      <button onClick={() => setStatus(req, STATUS.CLARIFICATION)} className="text-[11px] text-gray-400 hover:text-gray-600">Clarify</button>
                    )}
                  </div>,
                ],
              };
            })}
          />
          {!loading && requirements.length === 0 && <p className="py-10 text-center text-[12px] text-gray-400">No requirements</p>}
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-black/30" onClick={() => setSelected(null)} />
          <div className="w-[400px] bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-[14px] font-semibold text-gray-900">{selected.code || selected.name}</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <Badge status={label(REQUIREMENT_STATUS, selected.status)} />
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div><p className="text-gray-400">Brand</p><p className="font-medium">{selected.brandName || '—'}</p></div>
                <div><p className="text-gray-400">Product</p><p className="font-medium">{selected.name}</p></div>
                <div><p className="text-gray-400">Category</p><p className="font-medium">{label(REQUIREMENT_CATEGORY, selected.category)}</p></div>
                <div><p className="text-gray-400">Garment Type</p><p className="font-medium">{label(GARMENT_TYPE, selected.garmentType)}</p></div>
                <div><p className="text-gray-400">Quantity</p><p className="font-mono font-semibold">{selected.quantity ?? '—'} pcs</p></div>
                <div><p className="text-gray-400">Target Cost</p><p className="font-mono font-semibold">{formatINR(selected.targetCost)}/pc</p></div>
                <div><p className="text-gray-400">Launch Date</p><p className="font-medium">{formatDate(selected.launchDate)}</p></div>
                <div><p className="text-gray-400">Due Date</p><p className="font-medium">{formatDate(selected.dueDate)}</p></div>
                <div><p className="text-gray-400">Priority</p><p className="font-medium">{label(PRIORITY, selected.priority)}</p></div>
                <div><p className="text-gray-400">Coordinator</p><p className="font-medium">{selected.coordinator || '—'}</p></div>
                <div className="col-span-2"><p className="text-gray-400">Collection / Season</p><p className="font-medium">{[selected.collection, selected.season].filter(Boolean).join(' · ') || '—'}</p></div>
                {selected.concept && <div className="col-span-2"><p className="text-gray-400">Concept</p><p className="font-medium">{selected.concept}</p></div>}
              </div>
              <div className="flex gap-2 flex-wrap pt-2">
                {REVIEWABLE.includes(selected.status) && (
                  <button onClick={() => setStatus(selected, STATUS.ACCEPTED, 3)} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Accept</button>
                )}
                {selected.status !== STATUS.CLARIFICATION && (
                  <button onClick={() => setStatus(selected, STATUS.CLARIFICATION)} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-[#1b3a6b] border border-[#1b3a6b]/30 rounded-lg hover:bg-blue-50">Request Clarification</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
